import { ReactNode } from "react";

import {
  TbBook2,
  TbBooks,
  TbBrain,
  TbBulb,
  TbChartBar,
  TbChartTreemap,
  TbChecklist,
  TbChessBishop,
  TbEyeSearch,
  TbFilterSearch,
  TbFlask2,
  TbHome,
  TbLayoutDashboard,
  TbPencilCheck,
  TbReport,
  TbReportAnalytics,
  TbSettings,
  TbTemplate,
  TbTimelineEvent,
  TbTopologyStarRing3,
  TbTrophy,
  TbUserStar
} from "react-icons/tb";

import { coreRoutesConstants } from "modules/core/routes/routesConstants";
import { collaborationPrivateRoutesConstants } from "modules/collaboration/routes/private/routesConstants";
import { experimentsPrivateRoutesConstants } from "modules/experiments/routes/private/routesConstants";
import { ideasPrivateRoutesConstants } from "modules/ideas/routes/private/routesConstants";
import { projectsPrivateRoutesConstants } from "modules/projects/routes/private/routesConstants";
import { reportsPrivateRoutesConstants } from "modules/reports/routes/private/routesConstants";
import { researchPrivateRoutesConstants } from "modules/research/routes/private/routesConstants";
import { insightsPrivateRoutesConstants } from "modules/insights/routes/private/routesConstants";
import { strategyPrivateRoutesConstants } from "modules/strategy/routes/private/routesConstants";
import { governancePrivateRoutesConstants } from "modules/governance/routes/private/routesConstants";
import { implementationPrivateRoutesConstants } from "modules/implementation/routes/private/routesConstants";

import { CanAccess } from "modules/user/config/permissions/Can";
import { UserRole } from "modules/user/types/entities";

type MenuLink = {
  label: string;
  url: string;
  icon: ReactNode;
  hidden?: boolean;
  isSoon?: boolean;
  isNew?: boolean;
  activeRegex?: RegExp;
};

type MenuGroup = {
  label: string;
  roles: UserRole[];
  links: MenuLink[];
};

export const menuItems = ({
  can,
  projectId
}: {
  can: CanAccess;
  projectId: string;
}): {
  top: MenuLink[];
  groups: MenuGroup[];
  bottom: MenuLink[];
} => ({
  top: [
    {
      label: "Home",
      icon: <TbHome size={16} />,
      url: coreRoutesConstants.dashboard.href(projectId)
    },
    {
      label: "Project Stats",
      url: insightsPrivateRoutesConstants.project_stats.href(projectId),
      icon: <TbChartBar size={16} />
    },
    {
      label: "Insights Repository",
      url: insightsPrivateRoutesConstants.insights_repository.href(projectId),
      icon: <TbTopologyStarRing3 size={16} />,
      isNew: true
    }
  ],
  groups: [
    {
      label: "Alignment & Strategy",
      roles: ["master", "executive"],
      links: [
        {
          label: "Exec Dashboard",
          icon: <TbLayoutDashboard size={16} />,
          url: strategyPrivateRoutesConstants.exec_dashboard.href(),
          isSoon: true
        },
        {
          label: "Business Objectives",
          url: strategyPrivateRoutesConstants.objectives.href(projectId),
          icon: <TbTrophy size={16} />
        },
        {
          label: "Strategic Decisions",
          url: strategyPrivateRoutesConstants.decisions.href(),
          icon: <TbChessBishop size={16} />,
          isNew: true,
          hidden: !can({
            I: "read",
            a: "strategic_decision",
            usingBestRole: true,
            allowSuperAdmin: true
          })
        },
        {
          label: "Exec Reports",
          icon: <TbReportAnalytics size={16} />,
          url: strategyPrivateRoutesConstants.exec_reports.href(),
          isSoon: true
        }
      ]
    },

    {
      label: "Governance",
      roles: ["program_manager"],
      links: [
        {
          label: "Dashboard",
          url: governancePrivateRoutesConstants.governance_dashboard.href(),
          icon: <TbLayoutDashboard size={16} />,
          isSoon: true
        },
        {
          label: "Governance Center",
          icon: <TbTemplate size={16} />,
          url: governancePrivateRoutesConstants.governance_center.href(
            projectId
          ),
          isNew: true
        },
        {
          label: "People stats",
          url: governancePrivateRoutesConstants.people_stats.href(projectId),
          icon: <TbUserStar size={16} />,
          hidden: !can({ I: "read", a: "people_stats", usingBestRole: true })
        },
        {
          label: "Playbooks",
          url: governancePrivateRoutesConstants.playbooks.href(projectId),
          icon: <TbBooks size={16} />
        }
      ]
    },

    {
      label: "Research Operations",
      roles: [
        "staff",
        "team_viewer",
        "client",
        "client_viewer",
        "administrator"
      ],
      links: [
        {
          label: "Studies",
          url: researchPrivateRoutesConstants.root.href(projectId),
          icon: <TbBook2 size={16} />,
          activeRegex: /\/r\/app\/research(?!\/observations)/
        },
        {
          label: "Customer Insights",
          url: researchPrivateRoutesConstants.observations.href(projectId),
          icon: <TbEyeSearch size={16} />
        },
        {
          label: "Impact",
          url: "#",
          icon: <TbChartTreemap size={16} />
        }
      ]
    },

    {
      label: "Experiment Operations",
      roles: [
        "staff",
        "team_viewer",
        "client",
        "client_viewer",
        "administrator"
      ],
      links: [
        {
          label: "Brainstorming",
          url: collaborationPrivateRoutesConstants.brainstorming.href(
            projectId
          ),
          icon: <TbBrain size={16} />,
          isNew: true
        },
        {
          label: "Ideas & hypothesis",
          url: ideasPrivateRoutesConstants.root.href(projectId),
          icon: <TbBulb size={16} />
        },
        {
          label: "Experiments",
          url: experimentsPrivateRoutesConstants.root.href(projectId),
          icon: <TbFlask2 size={16} />
        },
        {
          label: "Roadmap",
          url: insightsPrivateRoutesConstants.roadmap.href(projectId),
          icon: <TbTimelineEvent size={16} />
        },
        {
          label: "Reports",
          url: reportsPrivateRoutesConstants.root.href(projectId),
          icon: <TbReport size={16} />
        },
        {
          label: "Implementation",
          icon: <TbPencilCheck size={16} />,
          url: implementationPrivateRoutesConstants.root.href(projectId),
          isSoon: true
        }
      ]
    }
  ],

  bottom: [
    {
      label: "Query Engine",
      url: insightsPrivateRoutesConstants.query_engine.href(projectId),
      icon: <TbFilterSearch size={16} />
    },
    {
      label: "To-Dos",
      url: collaborationPrivateRoutesConstants.todos.href(projectId),
      icon: <TbChecklist size={16} />
    },
    {
      label: "Project Settings",
      url: projectsPrivateRoutesConstants.settings.href(projectId),
      icon: <TbSettings size={16} />
    }
  ]
});
